import React, { useMemo, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { v4 as uuid } from "uuid";
import { Container, Spacing } from "../Containers";
import { ColorClassNames, ColorVariables } from "../Colors";
import { CircledQuestionIcon, LockIcon } from "../Images/icons";
import { baseLength } from "../Styles/globals";
import { OptionalFunction, ReactSelectOptions } from "../types";
import { Popup } from "../Popup";

export const selectStyles = (props?: DropdownProps) => {
  const optionSize = props?.isExtraSmall ? "24px" : props?.isSmall ? "36px" : "56px";
  return {
    option: (provided, state) => ({
      ...provided,
      borderBottom: `1px solid ${ColorVariables.coco1}`,
      color: state.isSelected ? "white" : "",
      backgroundColor: state.isSelected ? ColorVariables.mint7 : state.isFocused ? ColorVariables.mint3 : "white",
      "&:hover": {
        backgroundColor: `${ColorVariables.mint3}`,
      },
      "&:last-child": {
        borderBottom: 0,
      },
      textAlign: "left",
      cursor: "pointer",
      padding: "15px",
    }),
    placeholder: (base) => ({
      ...base,
      color: ColorVariables.coco5,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      minHeight: "100px",
      maxHeight: "400px",
    }),
    menuList: (base) => ({
      ...base,
      padding: "0px",
      borderRadius: "8px",
    }),
    container: (base, state) => ({
      ...base,
      width: "100%",
      minHeight: "auto",
      backgroundColor: "white",
      border: (() => {
        let borderSize = 1;
        let borderColor = ColorVariables.coco3;
        if (state.selectProps.error) {
          borderSize = 2;
          borderColor = ColorVariables.cherry7;
        }
        if (state.selectProps.isActive) {
          borderSize = 3;
          borderColor = ColorVariables.mint;
        }
        return `${borderSize}px solid ${borderColor}`;
      })(),
      borderRadius: 8,
    }),
    control: (base) => ({
      ...base,
      border: "none",
      borderWidth: "0px",
      borderRadius: "8px",
      cursor: "pointer",
      height: optionSize,
      paddingLeft: "16px",
      boxShadow: "none",
    }),
    input: (base) => ({
      ...base,
      margin: "0px",
      height: optionSize,
      padding: "0px",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.selectProps.isActive ? ColorVariables.mint : ColorVariables.coco5,
      transform: state.selectProps.isActive && "rotate(180deg)",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: "none",
    }),
    valueContainer: (base) => ({
      ...base,
      height: optionSize,
      padding: "0px",
      margin: "0px",
    }),
  };
};

export type DropdownProps = {
  name: string;
  autoComplete?: string;
  title?: string;
  showLock?: boolean;
  value: string | number;
  onChange: (val: ReactSelectOptions[0] | null) => any;
  handleBlur?: OptionalFunction;
  error?: boolean;
  creatable?: boolean;
  className?: string;
  options?: ReactSelectOptions;
  caption?: string;
  onFocus?: OptionalFunction;
  required?: boolean;
  cancellable?: boolean;
  helperTitle?: string;
  helperBody?: JSX.Element;
  isSmall?: boolean;
  isExtraSmall?: boolean;
  dropdownOptions?: {
    menuPlacement?: "top" | "bottom" | "auto";
  };
  saveInputOnBlur?: boolean;
  placeholder?: string;
  dropdownIndicator?: boolean;
  titleColor?: string;
};

const Dropdown = (props: DropdownProps) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isSmall = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isExtraSmall = false,
    name,
    autoComplete = "off",
    title = "",
    showLock = false,
    value = "",
    onChange,
    handleBlur,
    error = false,
    creatable = false,
    className = "",
    options = [],
    caption = "",
    onFocus,
    required = false,
    cancellable = false,
    helperTitle,
    helperBody,
    dropdownOptions = undefined,
    saveInputOnBlur = false,
    placeholder = "",
    dropdownIndicator = false,
    titleColor = ColorVariables.coco5,
  } = props;
  const [active, setActive] = useState(false);
  const [showHelperPopup, setShowHelperPopup] = useState(false);
  const showQuestion = helperBody || helperTitle;

  const toggleHighlight = () => {
    setActive(!active);
  };

  const handleOnFocus = () => {
    toggleHighlight();
    if (onFocus) {
      onFocus(true);
    }
  };

  const handleOnBlur = (e) => {
    // This is to fix the issue that the input will disappear with user click away from the dropdown
    if (e.target.value && saveInputOnBlur) {
      onChange({ label: e.target.value, value: e.target.value, __isNew__: true });
    }
    toggleHighlight();
    if (handleBlur) {
      handleBlur();
    }
  };

  const selectId = useMemo(() => uuid(), []);
  const optionFound = options.find((option) => option.value === value);
  let internalValue = value === null ? null : optionFound;
  if (creatable && !optionFound && value) {
    internalValue = {
      value,
      label: typeof value === "number" ? value.toString() : value,
    };
  }

  let dropdowncomponents = {};
  if (!dropdownIndicator) {
    dropdowncomponents = {
      DropdownIndicator: () => null,
    };
  }

  return (
    <Container className={`fullWidth input-form dropdown-form ${className}`}>
      <Popup
        content={helperBody}
        handlePopup={() => setShowHelperPopup(false)}
        header={helperTitle}
        showPopup={showHelperPopup}
      />
      <Container
        data-name={name}
        className="fullWidth"
        style={{
          maxWidth: baseLength * 150,
        }}
        $mobileMaxWidth={baseLength * 82.5}
      >
        {title && (
          <label htmlFor={selectId}>
            <p
              style={{
                color: active ? ColorVariables.mint : titleColor,
                fontWeight: active ? 600 : 400,
                paddingLeft: baseLength * 3,
                marginBottom: baseLength * 3,
              }}
              className={ColorClassNames.mint}
            >
              <span data-name="title">{title}</span>
              {required && (
                <span className={ColorClassNames.mint} data-name="required-symbol">
                  *
                </span>
              )}{" "}
              {showLock && (
                <span data-name="lock-symbol">
                  <LockIcon />
                </span>
              )}{" "}
              {showQuestion && (
                <span
                  role="button"
                  className="clickable"
                  style={{
                    position: "relative",
                    top: "2px",
                  }}
                  onClick={() => setShowHelperPopup(true)}
                  data-name="question-symbol"
                >
                  <CircledQuestionIcon />
                </span>
              )}
            </p>
          </label>
        )}

        {/* Silly hack to get the select input to autofill correctly */}
        {autoComplete !== "off" && (
          <input
            tabIndex={-1}
            onChange={(e) => {
              onChange({
                label: e.target.value,
                value: e.target.value,
              });
            }}
            type="text"
            style={{
              opacity: 0,
              position: "fixed",
              top: "-1000px",
              pointerEvents: "none",
            }}
            autoComplete={autoComplete}
            value={value}
          />
        )}
        {creatable ? (
          <CreatableSelect
            id={selectId}
            menuShouldScrollIntoView={false}
            styles={selectStyles(props)}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            error={error}
            options={options}
            onChange={onChange}
            value={internalValue}
            isClearable={cancellable}
            isActive={active}
            menuPlacement={dropdownOptions && dropdownOptions.menuPlacement ? dropdownOptions.menuPlacement : "auto"}
            formatCreateLabel={(e) => e}
            createOptionPosition="first"
            placeholder={placeholder}
            components={dropdowncomponents}
            blurInputOnSelect={false}
          />
        ) : (
          <Select
            id={selectId}
            menuShouldScrollIntoView={false}
            styles={selectStyles(props)}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            error={error}
            options={options as any}
            onChange={onChange}
            isClearable={cancellable}
            value={internalValue}
            isActive={active}
            blurInputOnSelect={false}
            menuPlacement={dropdownOptions && dropdownOptions.menuPlacement ? dropdownOptions.menuPlacement : "auto"}
            placeholder={placeholder}
            menuPortalTarget={document.body}
          />
        )}
      </Container>
      {caption?.length > 0 && (
        <Container
          className="fullWidth"
          maxWidth={baseLength * 150}
          $mobileMaxWidth={baseLength * 82.5}
          paddingLeft={baseLength * 2}
          paddingRight={baseLength * 2}
          color={error ? ColorVariables.cherry9 : ColorVariables.mint}
        >
          <Spacing $space={baseLength} $mobileSpace={baseLength} />
          <p className="small">{caption}</p>
        </Container>
      )}
    </Container>
  );
};

export { Dropdown };
